import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import logo from 'assets/img/we-raise-images/help-yateem.png';

// Mirrors the receipt PDF the stripe-webhook emails on payment
// (generateInvoicePdf in supabase/functions/stripe-webhook/index.ts) so a
// donor downloading from their profile gets the same document they were sent.

const BLUE = '#2c5282';
const SECTION_BG = '#edf2f7';
const SECTION_TEXT = '#2d3748';
const LABEL = '#4a5568';
const VALUE = '#1a202c';
const ROW_ALT = '#f7fafc';
const DIVIDER = '#e2e8f0';
const MUTED = '#718096';

const styles = StyleSheet.create({
  page: { padding: 42, fontSize: 10, fontFamily: 'Helvetica', color: VALUE },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  logo: { width: 70, height: 70, objectFit: 'contain' },
  title: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: BLUE, marginTop: 20 },
  headerRule: { borderBottomWidth: 1.5, borderBottomColor: BLUE, marginBottom: 18 },
  sectionBar: { backgroundColor: SECTION_BG, paddingVertical: 5, paddingHorizontal: 8, marginBottom: 10 },
  sectionTitle: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: SECTION_TEXT },
  section: { marginBottom: 16 },
  detailRow: { flexDirection: 'row', paddingVertical: 2, paddingHorizontal: 8 },
  detailLabel: { width: 150, fontFamily: 'Helvetica-Bold', color: LABEL },
  detailValue: { flex: 1, color: VALUE },
  tableHeader: { flexDirection: 'row', backgroundColor: BLUE, paddingVertical: 5, paddingHorizontal: 8 },
  tableHeaderText: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#ffffff' },
  tableRow: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: DIVIDER },
  tableRowAlt: { backgroundColor: ROW_ALT },
  colTitle: { flex: 3 },
  colType: { flex: 2.2 },
  colBilling: { flex: 1.4 },
  colAmount: { flex: 1.4, textAlign: 'right' },
  totals: { alignSelf: 'flex-end', width: 220, marginTop: 10 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 },
  totalLabel: { fontFamily: 'Helvetica-Bold', color: LABEL },
  totalRule: { borderBottomWidth: 0.5, borderBottomColor: DIVIDER, marginVertical: 3 },
  grandTotal: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: BLUE },
  footer: { position: 'absolute', bottom: 36, left: 42, right: 42, borderTopWidth: 0.5, borderTopColor: DIVIDER, paddingTop: 6, flexDirection: 'row', justifyContent: 'space-between', fontSize: 8, color: MUTED },
  footerRight: { textAlign: 'right' }
});

const formatMoney = amount => `£${Number(amount || 0).toFixed(2)}`;

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatStatus = status => {
  if (!status) return '-';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Same address handling as the emailed receipt: array-of-objects shape first,
// then flat object, then the plain primary_address string.
function donorAddress(donor) {
  const addr = donor?.address;
  if (Array.isArray(addr) && addr.length > 0) {
    return [addr[0].addressLine1, addr[0].addressLine2, addr[0].city, addr[0].postcode, addr[0].country]
      .filter(Boolean).join(', ');
  }
  if (addr && typeof addr === 'object') {
    return [addr.street, addr.street2, addr.city, addr.state, addr.postcode, addr.country]
      .filter(Boolean).join(', ');
  }
  return donor?.primary_address || '';
}

function DonationInvoice({ order, donor }) {
  const donorName = [donor?.first_name, donor?.last_name].filter(Boolean).join(' ') || '-';
  const address = donorAddress(donor);
  const details = order.order_details || [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text style={styles.title}>Receipt</Text>
        </View>
        <View style={styles.headerRule} />

        <View style={styles.section}>
          <View style={styles.sectionBar}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Donation ID:</Text>
            <Text style={styles.detailValue}>{order.simple_id || '-'}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Date:</Text>
            <Text style={styles.detailValue}>{formatDate(order.created_at)}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Status:</Text>
            <Text style={styles.detailValue}>{formatStatus(order.payment_status)}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Payment Method:</Text>
            <Text style={styles.detailValue}>{order.payment_method?.payment_name || '-'}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionBar}>
            <Text style={styles.sectionTitle}>Donor Information</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Name:</Text>
            <Text style={styles.detailValue}>{donorName}</Text>
          </View>
          {address ? (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Address:</Text>
              <Text style={styles.detailValue}>{address}</Text>
            </View>
          ) : null}
        </View>

        <View style={styles.section}>
          <View style={styles.sectionBar}>
            <Text style={styles.sectionTitle}>Order Items</Text>
          </View>
          <View style={styles.tableHeader}>
            <Text style={[styles.colTitle, styles.tableHeaderText]}>Package Title</Text>
            <Text style={[styles.colType, styles.tableHeaderText]}>Donation Type</Text>
            <Text style={[styles.colBilling, styles.tableHeaderText]}>Billing</Text>
            <Text style={[styles.colAmount, styles.tableHeaderText]}>Amount</Text>
          </View>
          {details.map((item, i) => (
            <View key={i} style={i % 2 === 1 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}>
              <Text style={styles.colTitle}>{item.package?.package_title || 'Quick Donation'}</Text>
              <Text style={styles.colType}>{item.donation_type?.donation_type_name || '-'}</Text>
              <Text style={styles.colBilling}>{formatStatus(item.billing_condition)}</Text>
              <Text style={styles.colAmount}>{formatMoney(item.amount)}</Text>
            </View>
          ))}

          <View style={styles.totals}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal:</Text>
              <Text>{formatMoney(order.subtotal)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Transaction Fee:</Text>
              <Text>{formatMoney(order.transaction_fees)}</Text>
            </View>
            <View style={styles.totalRule} />
            <View style={styles.totalRow}>
              <Text style={styles.grandTotal}>Total Amount:</Text>
              <Text style={styles.grandTotal}>{formatMoney(order.donation_amount)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Gift Aid:</Text>
              <Text>{order.gift_aid_eligible ? 'Eligible' : 'Not Eligible'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text>Generated: {new Date().toLocaleDateString('en-GB')}</Text>
          <View style={styles.footerRight}>
            <Text>info@helpyateem.org | +44 (0)207 550 6899</Text>
            <Text>Hyat Hub, Suite 13, Durning Hall, Earlham Grove, London, E7 9AB</Text>
            <Text>Charity Number: 1158571</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export { DonationInvoice };
